import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { supabase } from '../../services/supabaseService';
import { loadMfaState, type MfaState } from '../../services/mfaService';
import AuthenticatorForm from './AuthenticatorForm';

export default function MfaBoundary({ children, onLogout }: { children: ReactNode; onLogout: () => void }) {
  const [state, setState] = useState<MfaState | null>(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const mode = useRef<MfaState['mode'] | null>(null);
  const lastToken = useRef<string | null>(null);
  const lastUser = useRef<string | null>(null);
  const refresh = useCallback(async (block = true) => {
    const ticket = ++generation.current;
    if (block) setState(null);
    setError('');
    try { const next = await loadMfaState(); if (ticket === generation.current) { mode.current = next.mode; setState(next); } }
    catch (e) { if (ticket === generation.current) { setState(null); setError(e instanceof Error ? e.message : 'No se ha podido comprobar la seguridad de la cuenta.'); } }
  }, []);
  useEffect(() => {
    void refresh();
    let timer: ReturnType<typeof setTimeout>;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // Never await Auth calls inside its callback: Supabase holds a session lock.
      const token = session?.access_token || null;
      const userId = session?.user.id || null;
      if (event === 'INITIAL_SESSION') { lastToken.current = token; lastUser.current = userId; return; }
      if (event === 'SIGNED_IN' && token === lastToken.current) return;
      const accountChanged = userId !== lastUser.current;
      lastToken.current = token; lastUser.current = userId;
      ++generation.current;
      // Preserve an in-progress QR on ordinary token refresh/focus. No CRM
      // content is mounted in enrollment mode. Sign-out always clears it.
      const block = mode.current !== 'enroll' || event === 'SIGNED_OUT' || accountChanged;
      if (block) setState(null);
      clearTimeout(timer); timer = setTimeout(() => { void refresh(block); }, 0);
    });
    return () => { ++generation.current; clearTimeout(timer); data.subscription.unsubscribe(); };
  }, [refresh]);
  if (state?.mode === 'ready') return <>{children}</>;
  return <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6"><section className="w-full max-w-md rounded-2xl bg-white border shadow-sm p-8 space-y-5">
    <ShieldCheck className="w-10 h-10 text-blue-600" aria-hidden="true" />
    <h1 className="text-xl font-semibold">{state?.mode === 'enroll' ? 'Protege tu cuenta de superadministrador' : 'Verificación en dos pasos'}</h1>
    {state?.email && <p className="text-sm text-gray-600 break-all">{state.email}</p>}
    {!state && !error && <p role="status">Comprobando la seguridad de tu cuenta…</p>}
    {error && <><p role="alert" className="text-red-700 text-sm">{error}</p><button onClick={() => void refresh()} className="text-blue-700 underline">Reintentar</button></>}
    {state && <AuthenticatorForm key={`${state.mode}:${state.email}`} enroll={state.mode === 'enroll'} factors={state.factors} onVerified={() => void refresh()} />}
    <button type="button" onClick={onLogout} className="text-sm text-gray-600 underline">Cerrar sesión</button>
  </section></main>;
}
